/**
 * coachPlacement.ts — pure card-placement math for the guided-tour "coach
 * marks" system. No DOM, no React: every input is plain numbers, every
 * output is plain numbers. That purity is deliberate — it's what makes the
 * part most likely to be visibly wrong (does the card land on the right
 * side, does it stay inside the viewport) a jsdom-free unit test instead of
 * something only caught by eyeballing the running app.
 *
 * CoachMarkHost.tsx feeds this real `getBoundingClientRect()` numbers;
 * CoachMarkCard.tsx never imports this file's DOM-touching neighbours,
 * only these constants and the return shape.
 */

export interface CoachRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface CoachSize {
  width: number;
  height: number;
}

export type CoachPlacementSide = 'top' | 'bottom' | 'left' | 'right';

export interface PlacedCard {
  top: number;
  left: number;
  placement: CoachPlacementSide;
  /** Distance from the card's leading edge to the arrow tip, clamped to
   *  [ARROW_INSET, extent - ARROW_INSET]. Compensates for cross-axis
   *  clamping so the arrow still points at the anchor once the card itself
   *  has been shoved off-centre by a viewport edge. */
  arrowOffset: number;
  /** True when no side had room and we fell back to the side with the most
   *  free space — the card may overlap the anchor slightly. */
  cramped: boolean;
}

// Anchor → card.
export const GAP = 12;
// Card → viewport edge.
export const MARGIN = 12;
// The rendered arrow div (10×10, rotated 45deg).
export const ARROW = 10;
// Keeps the arrow off the card's 12px corner radius.
export const ARROW_INSET = 18;
export const CARD_W = 300;

// Layering — above every product surface (AppCardMenu's portaled modals
// 100, its dropdown 80, ui/Modal + PublishPopover 50, Sidebar 40) and below
// toasts at 9999, so an error toast is never buried by a tutorial. Exported
// from here so the number has exactly one home.
export const COACH_Z = 200;
export const COACH_RING_Z = 199;

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min; // degenerate viewport smaller than the card — don't invert
  return Math.min(Math.max(value, min), max);
}

function oppositeSide(side: CoachPlacementSide): CoachPlacementSide {
  switch (side) {
    case 'top': return 'bottom';
    case 'bottom': return 'top';
    case 'left': return 'right';
    case 'right': return 'left';
  }
}

// Free space between the anchor and the viewport edge on `side`, net of the
// margin the card must keep from that same edge.
function freeSpace(side: CoachPlacementSide, anchor: CoachRect, viewport: CoachSize): number {
  switch (side) {
    case 'top': return anchor.top - MARGIN;
    case 'bottom': return viewport.height - (anchor.top + anchor.height) - MARGIN;
    case 'left': return anchor.left - MARGIN;
    case 'right': return viewport.width - (anchor.left + anchor.width) - MARGIN;
  }
}

function neededSpace(side: CoachPlacementSide, card: CoachSize): number {
  return (side === 'top' || side === 'bottom' ? card.height : card.width) + GAP;
}

/**
 * Where should the card land, given an anchor rect, the card's own size, the
 * viewport, and a preferred side?
 *
 * 1. Candidates = [preferred, opposite, the other two] — first one that fits.
 * 2. None fit → the side with the most free space, `cramped: true`.
 * 3. Main axis flush at GAP from the anchor; cross axis centred on the
 *    anchor, then clamped to stay inside the viewport margins.
 * 4. arrowOffset re-centres on the anchor within the (possibly clamped)
 *    card, so the arrow still points at the right thing.
 */
export function placeCard(
  anchor: CoachRect,
  card: CoachSize,
  viewport: CoachSize,
  preferred: CoachPlacementSide,
): PlacedCard {
  const opposite = oppositeSide(preferred);
  const others = (['top', 'bottom', 'left', 'right'] as CoachPlacementSide[]).filter(
    (s) => s !== preferred && s !== opposite,
  );
  const candidates: CoachPlacementSide[] = [preferred, opposite, ...others];

  let placement = candidates.find((side) => freeSpace(side, anchor, viewport) >= neededSpace(side, card));
  let cramped = false;
  if (!placement) {
    cramped = true;
    placement = candidates.reduce((best, side) =>
      freeSpace(side, anchor, viewport) > freeSpace(best, anchor, viewport) ? side : best,
    candidates[0]);
  }

  let top: number;
  let left: number;

  if (placement === 'top' || placement === 'bottom') {
    top = placement === 'top' ? anchor.top - card.height - GAP : anchor.top + anchor.height + GAP;
    top = clamp(top, MARGIN, viewport.height - card.height - MARGIN);
    const centeredLeft = anchor.left + anchor.width / 2 - card.width / 2;
    left = clamp(centeredLeft, MARGIN, viewport.width - card.width - MARGIN);
  } else {
    left = placement === 'left' ? anchor.left - card.width - GAP : anchor.left + anchor.width + GAP;
    left = clamp(left, MARGIN, viewport.width - card.width - MARGIN);
    const centeredTop = anchor.top + anchor.height / 2 - card.height / 2;
    top = clamp(centeredTop, MARGIN, viewport.height - card.height - MARGIN);
  }

  let arrowOffset: number;
  if (placement === 'top' || placement === 'bottom') {
    const anchorCenterX = anchor.left + anchor.width / 2;
    arrowOffset = clamp(anchorCenterX - left, ARROW_INSET, card.width - ARROW_INSET);
  } else {
    const anchorCenterY = anchor.top + anchor.height / 2;
    arrowOffset = clamp(anchorCenterY - top, ARROW_INSET, card.height - ARROW_INSET);
  }

  return { top, left, placement, arrowOffset, cramped };
}
